import { useCallback } from 'react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'

import Step from '../../step/step'
import useOrder from '../../../../order/state/order/use-order'
import { Dedication } from '../../../../order/state/dedication/order-dedication-types'
import RadioGroupField from '../../../../ui/molecules/FormComponents/RadioGroupField'
import InputField from '../../../../ui/molecules/FormComponents/InputField'
import CheckboxField from '../../../../ui/molecules/FormComponents/CheckboxField'
import TextAreaField from '../../../../ui/molecules/FormComponents/TextAreaField'
import { ToggleSwitch } from '../../../../ui/atoms/ToggleSwitch'
import EcardSelector from '../../../../order/components/designation/EcardSelector'
import useConfig from '../../../../config/state/use-config'
import { ecard } from 'models/StudioCampaign'
import Row from '../../../../ui/atoms/Layout/Row'
import Column from '../../../../ui/atoms/Layout/Column'
import SelectField from '../../../../ui/molecules/FormComponents/SelectField'
import useURLParams from '../../../../url-params/state/use-url-params'
import useFlow from '../../../state/use-flow'
import { useAddressForm } from '../../../../order/components/address/useAddressForm'

const DedicationAddressFields = () => {
  const { t } = useTranslation()
  const { formState } = useFormContext<Dedication>()

  const addressForm = useAddressForm({
    fieldIds: {
      country: 'recipientCountry',
      addressLine1: 'recipientAddress',
      addressLine2: 'recipientAddress2',
      city: 'recipientCity',
      state: 'recipientState',
      zipCode: 'recipientPostalCode',
    },
    enableLine2: false, // Dedication step uses single address line
  })

  return (
    <>
      <Row>
        <Column>
          <InputField
            id="recipientAddress"
            label={t('DedicationStep.billingAddressLabel', 'Mailing address')}
            dataId="dedication-recipient-address-input"
            error={formState.errors.recipientAddress as { message: string }}
            inputRef={addressForm.googlePlacesRef}
            autoComplete={addressForm.autoComplete}
            onFocus={addressForm.handleOnFocus}
            onBlur={addressForm.handleOnBlur}
            onKeyDown={addressForm.handleKeyDown}
          />
        </Column>
      </Row>

      <Row>
        <Column>
          <InputField
            id="recipientPostalCode"
            dataId="dedication-recipient-postal-code-input"
            label={
              addressForm.watchCountry === 'US'
                ? t('DedicationStep.zipCodeLabel', 'Zip code')
                : t('DedicationStep.postalCodeLabel', 'Postal code')
            }
            error={formState.errors.recipientPostalCode as { message: string }}
          />
        </Column>
        <Column>
          <InputField
            id="recipientCity"
            label={t('DedicationStep.cityLabel', 'City')}
            dataId="dedication-recipient-city-input"
            error={formState.errors.recipientCity as { message: string }}
          />
        </Column>
      </Row>

      <RowStyled>
        <Column style={{ marginRight: 10 }}>
          <SelectField
            id="recipientState"
            label={addressForm.getStateLabel()}
            error={formState.errors.recipientState as { message: string }}
            options={addressForm.stateOptions}
            dataId="dedication-recipient-state-select"
          />
        </Column>
        <Column>
          <SelectField
            id="recipientCountry"
            label={t('DedicationStep.countryLabel', 'Country')}
            dataId="dedication-recipient-country-select"
            error={formState.errors.recipientCountry as { message: string }}
            options={addressForm.countryOptions}
          />
        </Column>
      </RowStyled>
    </>
  )
}

export default function DedicationStep() {
  const { t } = useTranslation()
  const flow = useFlow()
  const order = useOrder()
  const config = useConfig()
  const urlParams = useURLParams()

  const form = useForm<Dedication>({
    defaultValues: {
      ...order.dedication.value,
      honoreeName: urlParams.dedication ?? '',
      recipientCountry: 'US', // Default to United States
    },
    resolver: yupResolver(order.dedication.schema),
  })

  const handleCardSelect = useCallback(
    (ecardInfo: ecard) => {
      form.setValue('ecardId', ecardInfo.id)
    },
    [form],
  )

  const ecardMessageField = (
    <TextAreaField
      id="ecardMessage"
      label={t('DedicationStep.noteLabel', 'Note')}
      required={false}
      error={
        form.formState.errors.ecardMessage
          ? { message: form.formState.errors.ecardMessage.message || '' }
          : undefined
      }
      dataId="dedication-note-textarea"
    />
  )

  const onSubmit = useCallback(
    (formData: Dedication) => {
      order.dedication.save(formData)
      flow.goNextPostConversion()
    },
    [flow, order.dedication],
  )

  // Determine the title based on whether dedication name is provided
  const dedicationTitle = urlParams.dedication
    ? t('DedicationStep.completeTitle', 'Complete your dedication')
    : t('DedicationStep.dedicateTitle', 'Dedicate your donation')

  return (
    <Step.Root
      header={{
        headline: dedicationTitle,
      }}
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Step.Content>
            <div>
              <RadioGroupField
                id="type"
                label=""
                options={[
                  { key: 'honor', label: t('DedicationStep.honorOption', 'In honor of...') },
                  { key: 'memory', label: t('DedicationStep.memoryOption', 'In memory of...') },
                ]}
                dataId="dedication-type-radio-group"
              />

              <InputField
                required
                id="honoreeName"
                label={t('DedicationStep.honoreeNameLabel', 'Honoree name')}
                placeholder={t('DedicationStep.honoreeNamePlaceholder', 'John Smith')}
                error={
                  form.formState.errors.honoreeName
                    ? { message: form.formState.errors.honoreeName.message || '' }
                    : undefined
                }
                dataId="dedication-honoree-name-input"
              />
            </div>

            <FormSection>
              <FormSectionTitle>
                {t('DedicationStep.messageTitle', 'Add a note (optional)')}
              </FormSectionTitle>

              <FormSectionDescription>
                {t(
                  'DedicationStep.messageDescription',
                  'Send an email or mail a card along with your dedication.',
                )}
              </FormSectionDescription>

              <CheckboxField id="sendEmail" dataId="dedication-send-email-checkbox">
                {t('DedicationStep.sendEmailLabel', 'Send email')}
              </CheckboxField>
            </FormSection>

            {form.watch('sendEmail') && (
              <FormSection>
                <InputField
                  id="recipientEmailAddress"
                  label={t('DedicationStep.recipientEmailLabel', 'Recipient email')}
                  type="email"
                  required
                  error={
                    form.formState.errors.recipientEmailAddress
                      ? { message: form.formState.errors.recipientEmailAddress.message || '' }
                      : undefined
                  }
                  dataId="dedication-recipient-email-input"
                />

                <InputField
                  id="recipientName"
                  label={t('DedicationStep.recipientNameLabel', 'Recipient name')}
                  required
                  error={
                    form.formState.errors.recipientName
                      ? { message: form.formState.errors.recipientName.message || '' }
                      : undefined
                  }
                  dataId="dedication-recipient-name-input"
                />

                {ecardMessageField}

                <EcardToggleSection>
                  <EcardToggleLabel>
                    {t('DedicationStep.addEcardLabel', 'Add an e-card')}
                  </EcardToggleLabel>
                  <ToggleSwitch
                    checked={form.watch('addEmailEcard') || false}
                    onChange={() => form.setValue('addEmailEcard', !form.watch('addEmailEcard'))}
                    dataId="dedication-ecard-toggle"
                  />
                  <EcardToggleStatus>
                    {form.watch('addEmailEcard')
                      ? t('DedicationStep.ecardOn', 'On')
                      : t('DedicationStep.ecardOff', 'Off')}
                  </EcardToggleStatus>
                </EcardToggleSection>

                {form.watch('addEmailEcard') && (
                  <EcardSelector
                    ecardId={form.watch('ecardId')}
                    label={t('DedicationStep.ecardSelectorLabel', 'Choose an ecard')}
                    cardList={config.dedication.eCards}
                    radioGroupName="ecardId"
                    onCardSelect={handleCardSelect}
                    dataId="dedication-ecard-selector"
                  />
                )}
              </FormSection>
            )}

            <CheckboxField id="sendPrintedCard" dataId="dedication-mail-card-checkbox">
              {t('DedicationStep.mailCardLabel', 'Mail card')}
            </CheckboxField>

            {form.watch('sendPrintedCard') && (
              <div>
                <Row>
                  <Column>
                    <InputField
                      id="recipientName"
                      label={t('DedicationStep.recipientNameLabel', 'Recipient name')}
                      dataId="dedication-recipient-name-input"
                      error={form.formState.errors.recipientName as { message: string }}
                    />
                  </Column>
                </Row>

                {!form.watch('sendEmail') && ecardMessageField}

                <DedicationAddressFields />

                {form.watch('sendEmail') && (
                  <MailingAddressNote>
                    {t(
                      'DedicationStep.mailingAddressNote',
                      'The same note will be used for both your email and mailed card.',
                    )}
                  </MailingAddressNote>
                )}
              </div>
            )}
          </Step.Content>
          <Step.Buttons>
            <Step.Button
              type="button"
              variant="skip"
              onClick={() => flow.goNextPostConversion()}
              dataId="dedication-step-skip-button"
            >
              {t('DedicationStep.skipButton', 'Skip')}
            </Step.Button>
            <Step.Button type="submit" variant="submit" dataId="dedication-step-submit-button">
              {t('DedicationStep.submitButton', 'Save')}
            </Step.Button>
          </Step.Buttons>
        </form>
      </FormProvider>
    </Step.Root>
  )
}

const FormSection = styled.div`
  gap: 16px;
  display: flex;
  flex-direction: column;
`

const FormSectionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`

const FormSectionDescription = styled.p`
  margin: 0;
  font-size: 14px;
`

const EcardToggleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const EcardToggleLabel = styled.span`
  font-size: 14px;
  color: #333;
  font-weight: 500;
`

const EcardToggleStatus = styled.span`
  font-size: 14px;
`

const MailingAddressNote = styled.p`
  font-size: 14px;
  margin-top: 16px;
  margin-bottom: 0;
`

const RowStyled = styled.div`
  display: flex;
  flex-direction: row;

  // Media query for mobile
  @media (max-width: 767px) {
    flex-direction: column;

    > :nth-child(1) {
      margin-right: 0 !important;
    }

    > :nth-child(2) {
      width: 50%;
    }
  }
`
